import { createPagination } from "./utilities/paginationBuilder";
import { getFilterClause, createPartialURL } from "./utilities/urlBuilder";
import { Deserializer } from "jsonapi-serializer";
import { IOModule, IOModuleWrapper } from "../models/IOModule";
import { ListQuery } from "../models/ListQuery";
import { APIConnector } from './api/APIConnector';
import { genericError } from "../models/GenericError";
import { Name } from "../models/Name";
import { PaginationModel } from "../models/Pagination";
import { StoreEntityResult } from "../models/services/StoreEntityResult";
import { getDeviceLicenceStatus } from "./utilities/StatusBuilder";
import { INTERNAL_SERVER_ERROR, NO_CONTENT } from "../constants/response-status-codes";
import { DEVICE_CATEGORIES } from "../constants/device-constants";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";
const apiConnector = new APIConnector();

export const getIOModules = async (listParameters?: ListQuery): Promise<IOModuleWrapper> => {
    try {
        const filterCategory = `unitType.utCategory==${DEVICE_CATEGORIES.INPUT_OUTPUT}`;
        let filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        filter = filter ? `${filter};${filterCategory}` : `&filter=${filterCategory}`; 
        const partialUrl = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const includes = "include=unitType,site,profile,controller,location";
        const terminalFields = "fields[terminal]=devName,devFixedAddr,fwVersion,wanIpAddress,devEnabled,devCommsStatus,unitType,site,profile,controller,totalRelaysSupported,liftName";
        const unitTypeFields = "fields[unitType]=trnName,utNo";
        const siteFields = "fields[site]=siteName";
        const profileFields = "fields[profile]=trnName";
        const controllerFields = "fields[controller]=devName,devFixedAddr";
        const url = `terminal?${includes}&${terminalFields}&${unitTypeFields}&${siteFields}&${profileFields}&${controllerFields}${partialUrl}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const rows: IOModule[] = data.map((ioModule): IOModule => {

                    return {
                        id: ioModule['id'],
                        name: ioModule['dev-name'],
                        fixedAddress: ioModule['dev-fixed-addr'],
                        ipAddress: ioModule['wan-ip-address'] || '--',
                        type: ioModule['unit-type']['trn-name'],
                        site: ioModule['site']['site-name'],
                        preset: ioModule['profile']['trn-name'],
                        enabled: ioModule['dev-enabled'],
                        controller: ioModule['controller']['dev-name'],
                        controllerAddress: ioModule['controller']['dev-fixed-addr'],
                        status: getDeviceLicenceStatus(true, ioModule['dev-enabled'], ioModule['dev-comms-status']),
                        totalRelaysSupported: ioModule['total-relays-supported'],
                        unitTypeNo: ioModule['unit-type']['ut-no'],
                        liftName: ioModule['lift-name']? ioModule['lift-name'] : '-',
                        version: ioModule['fw-version']
                    }
                })

                return resolve({ rows, pagination });
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        })
    }
}

export const getIOModulesNamesWithPagnation = async (listQuery?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filterCategory = `unitType.utCategory==${DEVICE_CATEGORIES.INPUT_OUTPUT}`;
        let filters: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        filters = filters ? `${filters};${filterCategory}` : `&filter=${filterCategory}`; 
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filters);
        const includes = `include=deviceImprox`;
        const fieldsTerminal = `&fields[terminal]=devName`;
        const url = `terminal?${includes}${fieldsTerminal}${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, items: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const ioModules: Name[] = items.map((ioModule): Name => {
                    return {
                        value: ioModule['id'],
                        label: ioModule['dev-name']
                    };
                });

                return resolve({ rows: ioModules, pagination: newPagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        })
    }
}

export const getIOModuleById = async (ioModuleId: string): Promise<IOModule> => {
    try {
        const includes = "include=controller,unitType,site,profile,location,locationType,deviceImprox,modeType,threatLevel";
        const terminalFields = "fields[terminal]=devName,devDesc,devFixedAddr,devSla,devDirection,musteringPoint,devIpAddress,fwVersion,devEnabled,devCommsStatus,deviceImprox,location,locationType,controller,unitType,site,profile,modeType,threatLevel,liftName,usedByLift";
        const locationFields = "fields[location]=locName";
        const unitTypeFields = "fields[unitType]=trnName,utCategory,utNo";
        const siteFields = "fields[site]=siteName";
        const profileFields = "fields[profile]=trnName,profileSysnameType";
        const controllerFields = "fields[controller]=devName,devFixedAddr";
        const locationTypeFields = "fields[locationType]=ltName";
        const modeTypeFields = "fields[modeType]=trnName";
        const threatLevelFields = "fields[threatLevel]=tlName";
        const url = `terminal/${ioModuleId}?${includes}&${terminalFields}&${locationFields}&${unitTypeFields}&${siteFields}&${profileFields}&${controllerFields}&${locationTypeFields}&${modeTypeFields}&${threatLevelFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }
                const ioModule: IOModule = { 
                    id: data['id'],
                    name: data['dev-name'],
                    fixedAddress: data['dev-fixed-addr'],
                    ipAddress: data['dev-ip-address'] ? data['dev-ip-address'] : '-',
                    site: data['site']['site-name'],
                    siteId: data['site']['id'],
                    preset: data['profile']['trn-name'],
                    type: data['unit-type']['trn-name'],
                    typeId: data['unit-type']['id'],
                    unitTypeNo: data['unit-type']['ut-no'],
                    controller: data['controller']['dev-name'],
                    controllerAddress: data['controller']['dev-fixed-addr'],
                    logicalAddress: data['dev-sla'],
                    description: data['dev-desc'] ?? '',
                    terminalId: data['id'],
                    version: data['fw-version'],
                    enabled: data['dev-enabled'],
                    presetId: data['profile']['id'],
                    defaultMode: data['mode-type']['trn-name'],
                    defaultModeId: data['mode-type']['id'],
                    functionId: data['location-type']['id'],
                    function: data['location-type']['lt-name'],
                    threatLevelId: data['threat-level']['id'],
                    threatLevel: data['threat-level']['tl-name'],
                    positionKey: data['dev-direction'],
                    liftName: data['lift-name'] ? data['lift-name'] : '-',
                    isUsedByLift: data['used-by-lift'] ?? false
                }

                return resolve(ioModule);
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        })
    }
}

export const assignModulesToPreset = async (presetId: string,ioModulesToAdd: string[]): Promise<void> => {

    const modulesToAdd = ioModulesToAdd.map((moduleId) => ({
        type: "terminal",
        id: moduleId,
        relationships: {
            profile: {
               data:{ 
                type: "profile",
                id: presetId
               }
            },
        },
    }));

    const addedModules = modulesToAdd.map((module) =>
        apiConnector.sendData(`terminal/${module.id}`, false, {
            data: module,
        })
    );

     Promise.allSettled(addedModules);
};

export const storeIOModule = async (ioModule, createIOModule = true) => {
    try {
        const newRecord = {
            data: {
                type: 'terminal',
                id: ioModule.id,
                attributes: {
                    devDesc: ioModule.description,
                    devEnabled: !!ioModule.enabled,
                    devName: ioModule.name,
                },
                relationships: {
                    profile: {
                        data: {
                            type: "profile",
                            id: ioModule.presetId
                        }
                    },
                    threatLevel: {
                        data: {
                            type: "threatLevel",
                            id: ioModule.threatLevelId
                        }
                    }
                }
            }

        }

        const url = `terminal${createIOModule ? '' : `/${newRecord.data.id}`}`;
        let response: Response;

        await Promise
            .all([apiConnector.sendData(url, createIOModule, newRecord),
            apiConnector.sendData(`terminal/${ioModule.terminalId}`, false, getTerminalData(ioModule.terminalId, ioModule.functionId, ioModule.defaultModeId))])
            .then(data => {
                response = data[0];
            }).catch(error => {
                return new Promise((resolve, reject) => {
                    return reject(processServerError(error));
                })
            });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createIOModule) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                    }

                    new Deserializer().deserialize(content, (error, newIOModule) => {
                        if (error) {
                            return reject({ status: response.status, id: newIOModule.id, success: false });
                        }
                        return resolve({ id: newIOModule.id, success: true });
                    })
                    return;
                }
                if (response.status === NO_CONTENT) {
                    return resolve({ id: ioModule.id, success: true })
                }

                const content = await response.json();
                return reject({ status: response.status, id: ioModule.id, success: false, message: content.errors.map(error => getConstraintViolation(error.detail)) });
            }

            return reject({ status: INTERNAL_SERVER_ERROR, id: ioModule.id, success: false, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const getTerminalData = (terminalId: string, locationTypeId: string, modeTypeId: string) => {
    return {
        data: {
            type: "terminal",
            id: terminalId,
            relationships: {
                locationType: {
                    data: {
                        type: 'locationType',
                        id: locationTypeId
                    },
                },
                modeType: {
                    data: {
                        type: 'modeType',
                        id: modeTypeId
                    },
                }
            }
        }
    }
}

